'use client'

import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type RefObject } from 'react'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import {
  Download,
  LoaderCircle,
  FastForward,
  Pause,
  Play,
  Repeat2,
  ArrowLeftRight,
  ListStart,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'
import { getAssetKey, getAudioPreviewUrl, getThumbnail } from '@/lib/assets/assets'
import type { AssetSummary } from '@/lib/assets/types'
import { useAssetPopupStore } from '@/lib/store/asset-popup-store'
import { useAppStore } from '@/lib/store/store'
import { useAudioStore } from '@/lib/store/audio-store'
import { previewSpeeds, usePreviewPlayerStore } from '@/lib/store/preview-player-store'
import useAutoVideoPreviewsAllowed from '@/lib/hooks/use-auto-video-previews'
import { usePreviewPlayerJump } from '@/lib/hooks/use-preview-player-jump'
import type { useAudioPreviewNavigation } from '@/lib/hooks/use-audio-preview-navigation'
import { PreviewPlayerJumpButton } from './preview-player-jump-button'
import { PreviewPlayerFilmstrip } from './preview-player-filmstrip'
import { PreviewPlayerProgress } from './preview-player-progress'
import { usePreviewVideo } from '@/lib/hooks/use-preview-video'
import { useGridPageScroll } from '@/lib/hooks/use-grid-page-scroll'
import { useGridNavigationLoading } from '@/lib/hooks/use-grid-navigation-loading'
import { LoadingOutline } from '@/components/ui/loading-outline'
import { features } from '@/lib/config'
import styles from './preview-player-bar.module.scss'

export type PreviewPlayerNavigation = {
  navigate: (offset: number) => void
  begin: (offset: number) => void
  release: (offset: number) => void
  cancel: () => void
}

export type PreviewPlayerBarProps = {
  placement?: 'floating' | 'modal' | 'embedded'
  videoElement?: HTMLVideoElement | null
  navigationRef?: RefObject<PreviewPlayerNavigation | null>
  downloadAction?: { onClick: () => void; disabled: boolean; pending: boolean }
  onPlaybackIntentChange?: (playing: boolean) => void
  asset?: AssetSummary | null
  mediaReady?: boolean
  playbackIntent?: boolean
  pageLoading?: boolean
  mode?: 'video' | 'audio' | 'mixed'
  hasPrevious?: boolean
  hasNext?: boolean
  navigating?: boolean
  assets?: AssetSummary[]
  audioNavigation?: ReturnType<typeof useAudioPreviewNavigation>
  onNavigate?: (offset: number) => Promise<AssetSummary | undefined>
}

const emptyAssets: AssetSummary[] = []

const subscribeToClient = () => () => {}
const clientSnapshot = () => true
const serverSnapshot = () => false

export function PreviewPlayerBar(props: PreviewPlayerBarProps) {
  if (!features.compactCategoryNavigation) return null
  return <PreviewPlayerBarContent {...props} />
}

function PreviewPlayerBarContent({
  asset,
  placement = 'floating',
  videoElement,
  navigationRef,
  downloadAction,
  onPlaybackIntentChange,
  mode = 'video',
  hasPrevious,
  hasNext,
  navigating,
  onNavigate,
  assets = emptyAssets,
  mediaReady = true,
  playbackIntent = false,
  pageLoading = false,
  audioNavigation,
}: PreviewPlayerBarProps) {
  const expanded = placement === 'modal'
  const embedded = placement === 'embedded'
  const controlled = expanded || embedded
  const popupOpen = useAssetPopupStore((state) => state.isOpen)
  const active = !embedded || !popupOpen
  const sharedId = (id: string) => (embedded ? undefined : id)
  const reduceMotion = useReducedMotion()
  const layoutTransition = { duration: reduceMotion ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] as const }
  const mounted = useSyncExternalStore(subscribeToClient, clientSnapshot, serverSnapshot)
  const routeLoading = useGridNavigationLoading()
  const pages = useGridPageScroll(mounted && !controlled, !!reduceMotion)
  const buttonMotion = {
    whileHover: { scale: reduceMotion ? 1 : 1.08 },
    whileTap: { scale: reduceMotion ? 1 : 0.88 },
    transition: { duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' as const, layout: layoutTransition },
  }
  const reveal = {
    initial: { opacity: 0, scale: reduceMotion ? 1 : 0.7 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: reduceMotion ? 1 : 0.7 },
    transition: {
      opacity: { duration: reduceMotion ? 0 : 0.22, delay: reduceMotion ? 0 : 0.08 },
      scale: { duration: reduceMotion ? 0 : 0.2 },
      layout: layoutTransition,
      x: { duration: reduceMotion ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] as const },
    },
  }
  const autoAllowed = useAutoVideoPreviewsAllowed()
  const { preference, setPreference, sound, setSound } = useAppStore(
    useShallow((state) => ({
      preference: state.videoPreviewPreference,
      setPreference: state.setVideoPreviewPreference,
      sound: state.enableVfxPreviewSoundOnHover,
      setSound: state.setEnableVfxPreviewSoundOnHover,
    }))
  )
  const {
    video: modalVideo,
    advanceMode,
    setAdvanceMode,
    speed,
    setSpeed,
  } = usePreviewPlayerStore(
    useShallow((state) => ({
      video: state.video,
      advanceMode: state.advanceMode,
      setAdvanceMode: state.setAdvanceMode,
      speed: state.speed,
      setSpeed: state.setSpeed,
    }))
  )
  const video = embedded ? videoElement : modalVideo
  const loop = !expanded || advanceMode === 'repeat' || (advanceMode === 'bounce' && asset?.vertical === 'sounds')
  const advance = useRef<() => void>(() => {})
  const audio = useAudioStore(
    useShallow((state) => ({
      src: state.currentSrc,
      playing: state.isPlaying,
      play: state.play,
      pause: state.pause,
      setPlaybackOptions: state.setPlaybackOptions,
      loading: state.isLoading,
      endCount: state.endCount,
    }))
  )
  const isAudio = asset ? asset.vertical === 'sounds' : mode === 'audio' || (mode === 'mixed' && !!audio.src)
  const audioSrc = isAudio && asset ? getAudioPreviewUrl(asset) : isAudio ? audio.src : null
  const videoState = usePreviewVideo({
    video,
    enabled: controlled && active && !isAudio,
    bounce: expanded && advanceMode === 'bounce',
    speed,
    onEnded: () => advance.current(),
  })
  useEffect(() => {
    if (expanded && video && videoState.element === video && videoState.playing) {
      onPlaybackIntentChange?.(true)
    }
  }, [expanded, onPlaybackIntentChange, video, videoState.element, videoState.playing])
  const setPlaybackOptions = audio.setPlaybackOptions
  const currentAudio = !!audioSrc && audio.src === audioSrc
  const previewsPlaying = mounted && (preference === 'on' || (preference === 'auto' && autoAllowed))
  const playing = isAudio
    ? currentAudio && audio.playing
    : controlled
      ? videoState.element === video
        ? videoState.playing
        : expanded && playbackIntent
      : previewsPlaying
  const metadataDuration = asset ? Number(asset.properties?.seconds) || 0 : 0
  const duration = isAudio ? metadataDuration : videoState.duration
  const thumbnail = asset ? getThumbnail(asset) : null
  const activeAssetIndex = asset ? assets.findIndex((item) => getAssetKey(item) === getAssetKey(asset)) : 0
  const { jump, begin, release, cancel } = usePreviewPlayerJump({
    asset,
    assets,
    onNavigate,
    ready:
      mediaReady &&
      (isAudio ? currentAudio && !audio.loading : !!video && videoState.ready && videoState.element === video),
  })
  const [filmstripRevealed, setFilmstripRevealed] = useState(false)
  const [filmstripRange, setFilmstripRange] = useState<{ start: number; end: number } | null>(null)
  const filmstripHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const revealFilmstrip = useCallback(
    (offset: number) => {
      clearTimeout(filmstripHideTimeoutRef.current)
      const origin = Math.max(0, activeAssetIndex)
      const destination = Math.min(Math.max(0, origin + offset), Math.max(0, assets.length - 1))
      const start = Math.max(0, Math.min(origin, destination) - 4)
      const end = Math.min(assets.length, Math.max(origin, destination) + 5)
      setFilmstripRange((current) =>
        current ? { start: Math.min(current.start, start), end: Math.max(current.end, end) } : { start, end }
      )
      setFilmstripRevealed(true)
    },
    [activeAssetIndex, assets.length]
  )
  const scheduleFilmstripHide = useCallback(() => {
    clearTimeout(filmstripHideTimeoutRef.current)
    filmstripHideTimeoutRef.current = setTimeout(() => {
      setFilmstripRevealed(false)
      setFilmstripRange(null)
      filmstripHideTimeoutRef.current = undefined
    }, 500)
  }, [])
  const beginJump = useCallback(
    (offset: number) => {
      revealFilmstrip(offset)
      begin(offset)
    },
    [begin, revealFilmstrip]
  )
  const releaseJump = useCallback(
    (offset: number) => {
      revealFilmstrip(offset)
      const navigation = release(offset)
      scheduleFilmstripHide()
      return navigation
    },
    [release, revealFilmstrip, scheduleFilmstripHide]
  )
  const cancelJump = useCallback(() => {
    cancel()
    scheduleFilmstripHide()
  }, [cancel, scheduleFilmstripHide])

  useEffect(() => {
    if (expanded) return
    clearTimeout(filmstripHideTimeoutRef.current)
    setFilmstripRevealed(false)
    setFilmstripRange(null)
  }, [expanded])
  useEffect(() => () => clearTimeout(filmstripHideTimeoutRef.current), [])
  const showSingles = !embedded && (expanded || isAudio)
  const canPrevious = expanded ? hasPrevious : isAudio ? audioNavigation?.hasPrevious : false
  const canNext = expanded ? hasNext : isAudio ? audioNavigation?.hasNext : false
  const busy = routeLoading || pageLoading || !!navigating || (!expanded && !!audioNavigation?.navigating)
  const navigateSingle = (offset: number) => (expanded ? releaseJump(offset) : audioNavigation?.onNavigate(offset))
  useEffect(() => {
    if (!navigationRef) return
    navigationRef.current = {
      navigate: (offset) => {
        void releaseJump(offset)
      },
      begin: beginJump,
      release: (offset) => {
        void releaseJump(offset)
      },
      cancel: cancelJump,
    }
    return () => {
      navigationRef.current = null
    }
  }, [navigationRef, releaseJump, beginJump, cancelJump])
  const scrollPage = async (direction: -1 | 1) => {
    const scroller = await pages.scrollPage(direction)
    if (isAudio) await audioNavigation?.playFirstVisible(scroller)
  }
  advance.current = () => {
    if (
      !expanded ||
      advanceMode === 'repeat' ||
      advanceMode === 'bounce' ||
      !hasNext ||
      navigating ||
      !onNavigate ||
      jump?.phase === 'holding'
    )
      return
    const next = releaseJump(advanceMode === 'jump' ? 10 : 1)
    void next
      .then((target) => {
        if (!isAudio || !target) return
        const src = getAudioPreviewUrl(target)
        if (src) useAudioStore.getState().play(src)
      })
      .catch(() => {})
  }
  const previousEndCount = useRef(audio.endCount)
  useEffect(() => {
    if (audio.endCount !== previousEndCount.current) {
      previousEndCount.current = audio.endCount
      if (isAudio && currentAudio) advance.current()
    }
  }, [audio.endCount, isAudio, currentAudio])

  useEffect(() => {
    if (!controlled || !active) return
    const audio = useAudioStore.getState()
    if (!isAudio) {
      if (audio.currentSrc) audio.stop()
    } else if (audioSrc && audio.isPlaying && audio.currentSrc !== audioSrc) {
      audio.play(audioSrc)
    }
  }, [active, controlled, isAudio, audioSrc])

  useEffect(() => {
    if (!active || !isAudio) return
    setPlaybackOptions({ muted: !sound, loop, rate: speed })
    return () => setPlaybackOptions({ muted: false, loop: false, rate: 1 })
  }, [active, isAudio, setPlaybackOptions, sound, loop, speed])

  useEffect(() => {
    if (active && controlled && video) {
      video.muted = !sound
      video.loop = loop
      video.defaultPlaybackRate = speed
      video.playbackRate = speed
    }
  }, [active, controlled, video, speed, sound, loop])

  useEffect(() => {
    if (!embedded || !video || !popupOpen) return
    const resume = !video.paused
    video.pause()
    return () => {
      if (resume && video.isConnected) void video.play().catch(() => {})
    }
  }, [embedded, video, popupOpen])

  const togglePlayback = () => {
    if (isAudio) {
      if (!audioSrc) {
        if (!expanded) void audioNavigation?.onNavigate(1)
        return
      }
      if (playing) audio.pause()
      else audio.play(audioSrc)
    } else if (controlled && video) {
      if (playing) {
        if (expanded) onPlaybackIntentChange?.(false)
        videoState.pause()
      } else {
        if (expanded) onPlaybackIntentChange?.(true)
        videoState.play()
      }
    } else if (!controlled) {
      setPreference(previewsPlaying ? 'off' : 'on')
    }
  }

  const effectiveAdvanceMode = isAudio && advanceMode === 'bounce' ? 'repeat' : advanceMode
  const modeLabels = {
    repeat: 'Repeat current asset',
    bounce: 'Bounce current asset',
    next: 'Auto advance one asset',
    jump: 'Auto advance ten assets',
  }
  const modes = isAudio ? (['repeat', 'next', 'jump'] as const) : (['repeat', 'bounce', 'next', 'jump'] as const)
  const nextMode = modes[(modes.findIndex((mode) => mode === effectiveAdvanceMode) + 1) % modes.length]

  if (!mounted) return null

  const bar = (
    <div
      className={styles.positioner}
      data-expanded={expanded}
      data-embedded={embedded}
      data-slot="preview-player-positioner"
    >
      <motion.section
        layout
        layoutId={sharedId('asset-preview-player')}
        layoutDependency={`${expanded}:${isAudio && currentAudio}`}
        transition={{ layout: layoutTransition }}
        className={styles.dock}
        data-expanded={expanded}
        data-audio={isAudio}
        data-embedded={embedded}
        data-download={!!downloadAction}
        data-playback-direction={videoState.direction}
        data-loading={busy}
        data-slot="preview-player-bar"
        aria-label="Preview player"
        aria-busy={busy}
      >
        {busy && <LoadingOutline />}
        <AnimatePresence>
          {expanded && filmstripRevealed && (
            <PreviewPlayerFilmstrip
              assets={assets}
              range={filmstripRange ?? undefined}
              jump={
                jump ?? {
                  id: 0,
                  origin: Math.max(0, activeAssetIndex),
                  destination: Math.max(0, activeAssetIndex),
                  duration: 0,
                  phase: 'holding',
                }
              }
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {expanded && asset && (
            <motion.div key="identity" {...reveal} className={styles.identity}>
              {thumbnail && (
                <Image src={thumbnail} alt="" width={52} height={52} unoptimized className={styles.thumbnail} />
              )}
              <span className={styles.title}>{asset.name}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {(controlled || (isAudio && currentAudio)) && (
            <motion.div key="timeline" {...reveal} className={styles.progressContainer}>
              <PreviewPlayerProgress
                asset={asset ?? audioNavigation?.activeAsset}
                audioSrc={audioSrc}
                isAudio={isAudio}
                video={video}
                playing={playing}
                disabled={busy}
                duration={duration}
                getTime={videoState.time}
                play={videoState.play}
                pause={videoState.pause}
                seek={videoState.seek}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          layout="position"
          layoutDependency={`${expanded}:${isAudio && currentAudio}`}
          transition={{ layout: layoutTransition }}
          className={styles.transport}
        >
          {!embedded && !expanded && (
            <PreviewPlayerJumpButton
              direction={-1}
              expanded={expanded}
              disabled={busy || (expanded ? !hasPrevious : !pages.previous)}
              holding={jump?.phase === 'holding'}
              onBegin={beginJump}
              onRelease={releaseJump}
              onCancel={cancelJump}
              onScroll={(direction) => {
                void scrollPage(direction)
              }}
            />
          )}
          <motion.div
            layout="position"
            layoutId={sharedId('preview-player-core')}
            layoutDependency={expanded}
            transition={{ layout: layoutTransition }}
            className={styles.transportCore}
          >
            <AnimatePresence>
              {showSingles && (
                <motion.button
                  key="previous"
                  {...buttonMotion}
                  {...reveal}
                  type="button"
                  className={`${styles.button} ${styles.stepButton}`}
                  aria-label="Previous asset"
                  title="Previous asset"
                  initial={{ ...reveal.initial, x: 0 }}
                  animate={{ opacity: canPrevious && !busy ? 1 : 0.25, scale: 1, x: '-100%' }}
                  exit={{ opacity: 0, x: 0 }}
                  disabled={!canPrevious || busy}
                  onClick={() => {
                    void navigateSingle(-1)
                  }}
                >
                  <SkipBack size={21} fill="currentColor" />
                </motion.button>
              )}
            </AnimatePresence>
            <motion.button
              {...buttonMotion}
              type="button"
              className={styles.play}
              data-playing={playing}
              aria-label={playing ? 'Pause previews' : 'Play previews'}
              title={playing ? 'Pause previews' : 'Play previews'}
              disabled={busy || (isAudio ? !audioSrc && !audioNavigation?.hasNext : controlled && !video)}
              onClick={togglePlayback}
            >
              {playing ? (
                <Pause size={28} fill="currentColor" strokeWidth={0} />
              ) : (
                <Play size={28} fill="currentColor" strokeWidth={0} />
              )}
            </motion.button>
            <AnimatePresence>
              {showSingles && (
                <motion.button
                  key="next"
                  {...buttonMotion}
                  {...reveal}
                  type="button"
                  className={`${styles.button} ${styles.stepButton}`}
                  aria-label="Next asset"
                  title="Next asset"
                  initial={{ ...reveal.initial, x: 0 }}
                  animate={{ opacity: canNext && !busy ? 1 : 0.25, scale: 1, x: '100%' }}
                  exit={{ opacity: 0, x: 0 }}
                  disabled={!canNext || busy}
                  onClick={() => {
                    void navigateSingle(1)
                  }}
                >
                  <SkipForward size={21} fill="currentColor" />
                </motion.button>
              )}
            </AnimatePresence>
            {expanded && downloadAction && (
              <motion.button
                {...buttonMotion}
                {...reveal}
                key="download"
                type="button"
                className={`${styles.button} ${styles.stepButton} ${styles.downloadShortcut}`}
                initial={{ ...reveal.initial, x: 0 }}
                animate={{ opacity: downloadAction.disabled ? 0.25 : 1, scale: 1, x: '200%' }}
                aria-label={downloadAction.pending ? 'Downloading asset' : 'Download asset (D)'}
                data-tooltip="Download · D"
                aria-keyshortcuts="D"
                disabled={busy || downloadAction.disabled}
                onClick={downloadAction.onClick}
              >
                {downloadAction.pending ? (
                  <LoaderCircle size={20} className="tw-animate-spin" />
                ) : (
                  <Download size={21} />
                )}
              </motion.button>
            )}
          </motion.div>
          {!embedded && !expanded && (
            <PreviewPlayerJumpButton
              direction={1}
              expanded={expanded}
              disabled={busy || (expanded ? !hasNext : !pages.next)}
              holding={jump?.phase === 'holding'}
              onBegin={beginJump}
              onRelease={releaseJump}
              onCancel={cancelJump}
              onScroll={(direction) => {
                void scrollPage(direction)
              }}
            />
          )}
        </motion.div>

        <motion.div
          layout="position"
          layoutDependency={`${expanded}:${isAudio && currentAudio}`}
          transition={{ layout: layoutTransition }}
          className={styles.options}
        >
          <motion.button
            {...buttonMotion}
            layoutId={sharedId('preview-player-speed')}
            type="button"
            className={`${styles.button} ${styles.speed}`}
            aria-label={`Playback speed ${speed}x`}
            title={`Playback speed ${speed}×`}
            disabled={busy}
            onClick={() => setSpeed(previewSpeeds[(previewSpeeds.indexOf(speed) + 1) % previewSpeeds.length])}
          >
            {speed}×
          </motion.button>
          {expanded && (
            <motion.button
              {...buttonMotion}
              layoutId="preview-player-loop"
              type="button"
              className={styles.button}
              aria-label={modeLabels[effectiveAdvanceMode]}
              title={`${modeLabels[effectiveAdvanceMode]} · click for ${modeLabels[nextMode].toLowerCase()}`}
              data-advance-mode={effectiveAdvanceMode}
              disabled={busy}
              onClick={() => setAdvanceMode(nextMode)}
            >
              {effectiveAdvanceMode === 'repeat' ? (
                <Repeat2 size={19} />
              ) : effectiveAdvanceMode === 'bounce' ? (
                <ArrowLeftRight size={19} />
              ) : effectiveAdvanceMode === 'next' ? (
                <ListStart size={19} />
              ) : (
                <FastForward size={19} fill="currentColor" strokeWidth={0} />
              )}
              {effectiveAdvanceMode === 'jump' && <span className={styles.modeBadge}>10</span>}
            </motion.button>
          )}
          <motion.button
            {...buttonMotion}
            layoutId={sharedId('preview-player-sound')}
            type="button"
            className={styles.button}
            aria-label={`Preview sounds ${sound ? 'On' : 'Off'}`}
            title={`Preview sounds ${sound ? 'On' : 'Off'}`}
            aria-pressed={sound}
            disabled={busy}
            onClick={() => setSound(!sound)}
          >
            {sound ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </motion.button>
        </motion.div>
        {expanded && videoState.error && !isAudio && (
          <span className={styles.status} role="status">
            Preview unavailable
          </span>
        )}
      </motion.section>
    </div>
  )

  return controlled ? bar : createPortal(bar, document.body)
}
